import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { selectIsImpersonating } from '@/features/auth/impersonation'
import { useExitImpersonation } from '@/features/auth/useExitImpersonation'
import {
  ADMIN_BLOCKED_PARAM,
  ADMIN_BLOCKED_VALUE,
  IMPERSONATION_ADMIN_BLOCKED,
  IMPERSONATION_EXIT,
} from '@/features/auth/impersonationCopy'

/** One toast however often the redirect lands. */
const TOAST_ID = 'impersonation-admin-blocked'

/**
 * Opening /admin while impersonating redirects to the app with
 * `?impersonation=admin_blocked` (backend brief, 86e39gctg). Once `/auth/me`
 * has answered, this strips the param and, while the impersonation still
 * holds, explains why with an Exit action. Renders nothing.
 *
 * @returns {null}
 */
export default function ImpersonationAdminBlockedNotice() {
  const [searchParams, setSearchParams] = useSearchParams()
  const isImpersonating = useSelector(selectIsImpersonating)
  // The redirect is a full page load: until /auth/me has answered, the
  // impersonation isn't known yet, so the param waits for it.
  const isSessionVerified = useSelector((state) => state.auth?.isSessionVerified === true)
  const { exit } = useExitImpersonation()
  const isBlocked = searchParams.get(ADMIN_BLOCKED_PARAM) === ADMIN_BLOCKED_VALUE

  useEffect(() => {
    if (!isBlocked || !isSessionVerified) return
    setSearchParams((params) => {
      const next = new URLSearchParams(params)
      next.delete(ADMIN_BLOCKED_PARAM)
      return next
    }, { replace: true })
    if (!isImpersonating) return
    toast.error((t) => (
      <span className="flex items-center gap-3">
        <span>{IMPERSONATION_ADMIN_BLOCKED}</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="shrink-0"
          onClick={() => {
            toast.dismiss(t.id)
            exit()
          }}
        >
          {IMPERSONATION_EXIT}
        </Button>
      </span>
    ), { id: TOAST_ID })
  }, [isBlocked, isSessionVerified, isImpersonating, setSearchParams, exit])

  return null
}
